import { Component, ErrorInfo, ReactNode } from 'react'

import { Button } from '@ui/components/shadcn/ui/button'

import { notifyClient } from './client'

interface ErrorBoundaryProps {
  children?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

// React error boundaries are the only way to catch render-phase errors.
// Responsibilities split per the React docs:
//   - getDerivedStateFromError: flip state so render() shows the fallback.
//     Without it React unmounts the entire subtree on a caught error — a
//     permanent blank page (verified empirically on react-dom 18.3.1).
//   - componentDidCatch: side effects — report to Airbrake with
//     info.componentStack, React's tree path. It is only available here and
//     is far more debuggable than the JS stack alone.
// Boundary-caught errors do NOT reach window.onerror on react-dom 18.3.1
// (reportError is only the default onRecoverableError, i.e. hydration
// recoveries), so this notify is the single notice for a render crash — no
// dedup against the global handlers is needed.
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error({ error, info })
    notifyClient({
      error,
      params: { info },
      context: { component: 'ErrorBoundary', severity: 'error' },
    })
  }

  render() {
    if (this.state.hasError) {
      // This boundary wraps the whole app in _app.tsx, so the fallback
      // renders OUTSIDE every provider — keep it dependency-light: Tailwind
      // classes and shadcn primitives only.
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
          <h1 className="text-2xl font-semibold">Something went wrong</h1>
          <p className="text-muted-foreground">
            An unexpected error occurred. Reloading the page usually fixes it.
          </p>
          <Button onClick={() => window.location.reload()}>Reload page</Button>
        </div>
      )
    }
    return this.props.children || null
  }
}

export default ErrorBoundary
